// Single source of truth for the participant-merge plumbing.
//
// Used by:
//   - Home.js handleApproveMerge (resolving a profile_match_suggestions row)
//   - Participants.js executeMerge (manual select-two-and-merge from People page)
//
// Direction (which side keeps its UUID, which is soft-deleted) is the caller's
// responsibility. Pass keepId = the record to survive, dropId = the record
// to soft-delete.
//
// Implementation (since mig 86): one supabase.rpc call to
// merge_participants_atomic. The whole merge runs inside a SECURITY DEFINER
// function — one transaction, elevated authority, no per-table RLS gate
// participates. Replaces the previous 10+ direct PostgREST UPDATE pattern
// that was vulnerable to silent 0-row UPDATEs when caller RLS happened to
// disagree with operation-level authorization (the Ben/Benjamin Fanning
// incident, May 15 2026). See migrations/86_merge_participants_atomic_up.sql
// for the design rationale.
//
// Returns { ok, error, debug? }. `debug` carries per-table affected-row
// counts from the RPC's return payload on successful merges — useful for
// post-incident inspection if anything ever looks off again.
//
// Authorization is checked server-side by the RPC. The caller does not
// need to be in coord-mode in general — added_by on either participant
// suffices for own-duplicate resolution.

import { supabase } from '../supabaseClient'

export async function mergeTwoParticipants(keepId, dropId, resolutions = null) {
  if (!keepId || !dropId) {
    return { ok: false, error: 'Missing keep or drop id' }
  }
  if (keepId === dropId) {
    return { ok: false, error: 'Keep and drop refer to the same participant' }
  }

  const { data, error } = await supabase.rpc('merge_participants_atomic', {
    p_keep_id:     keepId,
    p_drop_id:     dropId,
    p_resolutions: resolutions || null,
  })

  if (error) {
    return { ok: false, error: error.message }
  }

  const row = (data || [])[0]
  if (!row) {
    return { ok: false, error: 'merge RPC returned no rows' }
  }

  if (!row.success) {
    return { ok: false, error: row.reason || 'merge failed', debug: row.debug_counts }
  }

  return { ok: true, error: null, debug: row.debug_counts }
}
